// import Vue from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { useCookies } from 'vue3-cookies';
import store from '@/store';

import Home from '@/views/Home';
import Profile from '@/views/Profile';
import Search from '@/views/Search';
import Reservations from '@/views/Reservations';
import Services from '@/views/Services';
import Documents from '@/views/Documents';

import MyContacts from '@/components/profile/MyContacts';
import MyProfile from '@/components/profile/MyProfile';
import ContactForm from '@/components/profile/ContactForm';
import ProfileForm from '@/components/profile/ProfileForm';

import ContactPreferences from '@/components/profile/ContactPreferences';

import eBillets from '@/components/reservations/eBillets';
import MyOrders from '@/components/reservations/MyOrders';
import MyOptions from '@/components/reservations/MyOptions';
import OrderDetails from '@/components/reservations/OrderDetails';
import ImportDossier from '@/components/reservations/ImportDossier';

import PayboxGateway from '@/components/common/PayboxGateway';

import WalletsList from '@/components/services/WalletsList';
import CarryWallet from '@/components/services/CarryWallet';
import ShareWallet from '@/components/services/ShareWallet';
import WalletHistory from '@/components/services/WalletHistory';
import WalletBalance from '@/components/services/WalletBalance';
import WalletForm from '@/components/services/WalletForm';
import Formalities from '@/components/services/Formalities';
import Referrals from '@/components/referrals/Referrals';
import NewReferral from '@/components/referrals/NewReferral';
import ReferralCodes from '@/components/referrals/ReferralCodes';
import MyReferrals from '@/components/referrals/MyReferrals';
import HelpPage from '@/components/referrals/HelpPage';

import { isImpersonateMode } from '@/mixins/requests';

const routes = [
  {
    name: 'Home',
    path: '/:locale?/accueil',
    component: Home,
    meta: {
      icon: 'icon-accueil',
      visible: true,
      breadcrumb: false,
      reference: 'HOME'
    }
  },
  {
    name: 'Profile',
    path: '/:locale?/profil',
    component: Profile,
    meta: {
      icon: 'icon-profile',
      visible: true,
      breadcrumb: false,
      reference: 'PROFILE'
    },
    children: [
      {
        name: 'MyProfile',
        path: 'informations-personnelles',
        component: MyProfile,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'PERSONAL_DETAILS'
        },
        children: [
          {
            name: 'EditMyProfile',
            path: 'editer',
            component: ProfileForm,
            meta: {
              breadcrumb: true,
              reference: 'EDIT_YOUR_PERSONAL_DETAILS'
            }
          }
        ]
      },
      {
        name: 'ContactPreferences',
        path: 'preferences-contact',
        component: ContactPreferences,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'EMAIL_PREFERENCES'
        }
      },
      {
        name: 'MyContacts',
        path: 'mes-proches',
        component: MyContacts,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'OTHER_TRAVELLERS'
        },
        children: [
          {
            name: 'AddContact',
            path: 'nouveau',
            component: ContactForm,
            meta: {
              breadcrumb: true,
              reference: 'ADD_RELATIVE'
            }
          },
          {
            name: 'EditContact',
            path: ':uuid',
            component: ContactForm,
            meta: {
              breadcrumb: true,
              reference: 'Détails de Votre Proche'
            }
          }
        ]
      },
      {
        name: 'EmergencyContact',
        path: 'contact-urgence',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: "Contact en cas d'urgence"
        }
      }
    ]
  },
  {
    name: 'Search',
    path: '/:locale?/rechercher',
    component: Search,
    meta: {
      icon: 'icon-loupe',
      visible: false,
      breadcrumb: false,
      reference: 'Rechercher'
    }
  },
  {
    name: 'Reservations',
    path: '/:locale?/reservations',
    component: Reservations,
    meta: {
      icon: 'icon-resa',
      visible: true,
      breadcrumb: false,
      reference: 'BOOKINGS'
    },
    children: [
      {
        name: 'MyOrders',
        path: 'mes-achats',
        component: MyOrders,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'ALL_BOOKINGS'
        },
        children: [
          {
            name: 'DetailsOrder',
            path: ':uuid',
            component: OrderDetails,
            meta: {
              breadcrumb: true,
              reference: 'ACHAT'
            }
          }
        ]
      },
      {
        name: 'MyOptions',
        path: 'mes-options',
        component: MyOptions,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'OPTION_BOOKINGS'
        },
        children: [
          {
            name: 'DetailsOrderOptions',
            path: ':uuid',
            component: OrderDetails,
            meta: {
              breadcrumb: true,
              reference: 'Achat'
            }
          }
        ]
      },
      {
        name: 'AddImport',
        path: 'additiondossier',
        component: ImportDossier,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'IMPORT_BOOKINGS'
        }
      },
      {
        name: 'BookSlot',
        path: 'reserver-creneau',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Réserver un créneau'
        }
      },
      {
        name: 'eBillets',
        path: 'e-billets',
        component: eBillets,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'e-Billets'
        }
      },
      {
        name: 'Events',
        path: 'evenements',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Evénements'
        }
      },
      {
        name: 'Favorites',
        path: 'favoris',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Favoris'
        }
      }
    ]
  },
  {
    name: 'Hello Chat',
    path: '/:locale?/hello',
    component: Services,
    meta: {
      icon: 'icon-tchats',
      visible: true,
      breadcrumb: false,
      reference: 'MY_CHATS'
    },
    beforeEnter: (to, from, next) => {
      const { cookies } = useCookies();
      window.open(`${process.env.VUE_APP_HELLO_URL}?token=${cookies.get('oauth2_cookie')}`);
      next({ name: from.name });
    }
  },
  {
    name: 'Services',
    path: '/:locale?/plus',
    component: Services,
    meta: {
      icon: 'icon-plus',
      visible: true,
      breadcrumb: false,
      reference: 'Services'
    },
    children: [
      {
        name: 'Parrainage',
        path: 'parrainage',
        component: Referrals,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'PARRAINAGE'
        },
        children: [
          {
            name: 'nouveau',
            path: 'nouveau',
            component: NewReferral,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'PARRAINAGE_NEW'
            }
          },
          {
            name: 'mes',
            path: 'mes',
            component: MyReferrals,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'PARRAINAGE_CODES'
            }
          },
          {
            name: 'tous',
            path: 'tous',
            component: ReferralCodes,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'PARRAINAGE_MY'
            }
          },
          {
            name: 'HelpPage',
            path: 'help',
            component: HelpPage,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'HELP_PAGE_PARRAINAGE'
            }
          }

        ]
      },
      {
        name: 'AddWallet',
        path: 'nouveau-wallet',
        component: WalletForm,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'CREATE_NEW_ELECTRONIC_WALLET'
        },
        children: []
      },
      {
        name: 'Wallets List',
        path: 'wallets-list',
        component: WalletsList,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'ELECTRONIC_WALLET'
        },
        children: [
          {
            name: 'Carry',
            path: 'carry/:id',
            component: CarryWallet,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'CARRY_WALLET'
            }
          },
          {
            name: 'Share QRcode',
            path: 'share/:id',
            component: ShareWallet,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'SHARE_QRCODE'
            }
          },
          {
            name: 'Spending History',
            path: 'spending-history/:id',
            component: WalletHistory,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'CHECK_HISTORY'
            }
          },
          {
            name: 'Check Balance',
            path: 'check-balance/:id',
            component: WalletBalance,
            meta: {
              visible: true,
              breadcrumb: true,
              reference: 'MAKE_REFUND'
            }
          },
          {
            name: 'Gateway',
            path: 'gateway',
            component: PayboxGateway,
            meta: {
              icon: 'icon-accueil',
              visible: false,
              breadcrumb: true,
              reference: 'Recharger le porte-monnaie'
            }
          }

        ]
      },
      {
        name: 'Formalities',
        path: 'formalities',
        component: Formalities,
        meta: {
          visible: true,
          breadcrumb: true,
          reference: 'FORMALITIES'
        },
        children: []
      },
      {
        name: 'DiscussionGroupes',
        path: 'groupes-discussion',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Groupes de discussion'
        }
      },
      {
        name: 'ChildrenNews',
        path: 'nouvelles-mes-enfants',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Les nouvelles de mes enfants'
        }
      },
      {
        name: 'TransportInfo',
        path: 'infos-transport',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Infos transport'
        }
      },
      {
        name: 'GroupDevis',
        path: 'devis-groupe',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Devis groupe et CE'
        }
      },
      {
        name: 'Help',
        path: 'aide',
        component: Profile,
        meta: {
          visible: false,
          breadcrumb: true,
          reference: 'Aide'
        }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/accueil'
  }
];

const router = createRouter({
  history: createWebHistory('/espacepersonnel'),
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (to.hash) {
      document.getElementById(`${to.hash.substring(1, to.hash.length)}`).scrollIntoView({ behavior: 'smooth' });
    }
  }
});

// router.afterEach((to, from) => {
//   // document.getElementById('main-app').scrollIntoView();
//   // window.scrollTo( 0, 0 );
//   // console.log('scroll')
// });

router.beforeEach(async (to, from, next) => {
  const disableRouters = store.getters.getSiteConfiguration('disabled_features');
  const routePath = to.fullPath.split('/');
  const index = routePath.length;

  if (to.query.agency) {
    store.commit('setAgency', to.query.agency);
  }

  if (to.query.impersonateData || store.state.impersonateData) {
    const response = await isImpersonateMode(to.query.impersonateData || store.state.impersonateData);

    if (!response.success) {
      store.commit('setImpersonateData', null);
    }
  }

  // NOT ALLOW TO DISBALE ROUTES
  if (routePath[index - 1] && disableRouters.includes(routePath[index - 1])) {
    next('Home');
    return;
  }

  let language = store.getters.getLanguage;

  if (language !== 'fr' && !to.params.locale) {
    next({ name: to.name, params: { ...to.params, locale: language }, query: { ...to.query } });
    return;
  }

  // Locale diferent on URL
  if (to.params.locale && language !== to.params.locale) {
    language = store.state.allowedLanguages.includes(to.params.locale) ? to.params.locale : 'fr';
    store.commit('setSelectedLanguage', language);
    next({ name: to.name, params: { ...to.params, locale: language }, query: { ...to.query } });
    return;
  }

  next();
});

export default router;
